import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const LEAGUES = ["NFL", "NBA", "NHL", "MLB"];

function practiceStyle(p) {
  if (p === "DNP") return { color: "#F87171", bg: "#F8717118", label: "DNP" };
  if (p === "Limited") return { color: "#FB923C", bg: "#FB923C18", label: "Limited" };
  if (p === "Full") return { color: "#00E5A3", bg: "#00E5A318", label: "Full" };
  return { color: "#64748B", bg: "#64748B18", label: "—" };
}
function availStyle(s) {
  const t = (s || "").toLowerCase();
  if (t.includes("out") || t.includes("injured")) return { color: "#F87171", bg: "#F8717118" };
  if (t.includes("doubt")) return { color: "#FB923C", bg: "#FB923C18" };
  if (t.includes("day") || t.includes("question")) return { color: "#FFB020", bg: "#FFB02018" };
  return { color: "#64748B", bg: "#64748B18" };
}

function PlayerRow({ p, kind }) {
  const isPractice = kind === "practice";
  const st = isPractice ? practiceStyle(p.practice) : availStyle(p.injury_status);
  const badge = isPractice ? st.label : (p.injury_status || "—");
  return (
    <div data-testid={`practice-player-${p.player_id || p.name}`} className="flex items-center gap-3 py-2.5 border-b border-[#1A2133] last:border-0">
      {p.headshot
        ? <img src={p.headshot} alt="" className="h-9 w-9 rounded-full object-cover bg-[#151B2B] shrink-0" />
        : <div className="h-9 w-9 rounded-full bg-[#151B2B] shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {p.player_id
            ? <Link to={`/players/${p.player_id}`} className="text-sm font-semibold text-slate-100 hover:text-[#00E5A3] truncate">{p.name}</Link>
            : <span className="text-sm font-semibold text-slate-100 truncate">{p.name}</span>}
          {p.position && <span className="text-[10px] font-mono text-slate-500">{p.position}</span>}
          {isPractice && p.declared_inactive && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded text-[#F87171] bg-[#F8717118]">Inactive</span>}
        </div>
        {(p.body_part || p.game_status || p.practice_note || p.notes) && (
          <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-x-2">
            {p.game_status && <span className="text-slate-300">{p.game_status}</span>}
            {p.body_part && <span>{p.body_part}</span>}
            {(p.practice_note || p.notes) && <span className="text-slate-500 truncate">{p.practice_note || p.notes}</span>}
          </div>
        )}
      </div>
      <span className="text-[10px] font-mono uppercase px-2 py-1 rounded shrink-0" style={{ color: st.color, background: st.bg }}>
        {badge}
      </span>
    </div>
  );
}

export default function PracticeReport() {
  const [league, setLeague] = useState("NFL");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/practice-report?league=${league}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ available: false, reason: "Unable to load the report." }))
      .finally(() => setLoading(false));
  }, [league]);

  const isNFL = league === "NFL";

  return (
    <div data-testid="practice-report-page" className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 mb-1 flex items-center gap-2">
        <ClipboardList size={22} className="text-[#00E5A3]" /> {isNFL ? "NFL Practice Report" : `${league} Availability`}
        <Badge className="bg-[#38BDF8]/15 text-[#38BDF8] border-0 text-[10px] font-mono">SportsDataIO</Badge>
      </h1>
      <p className="text-sm text-slate-400 mb-4 max-w-2xl">
        {isNFL
          ? <>Wednesday–Friday practice participation — <span className="text-[#F87171]">DNP</span>, <span className="text-[#FB923C]">Limited</span> and <span className="text-[#00E5A3]">Full</span> — the NFL's signature availability signal, from SportsDataIO's licensed feed.</>
          : `Pregame availability and injury status for every ${league} player on the report, from SportsDataIO's licensed feed.`}
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {LEAGUES.map((lg) => (
          <button key={lg} data-testid={`practice-league-${lg}`} onClick={() => setLeague(lg)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${league === lg ? "bg-[#38BDF8] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
            {lg}
          </button>
        ))}
      </div>

      {!loading && data?.scrambled && (
        <div data-testid="practice-scrambled-banner" className="mb-5 rounded-xl border border-[#FB923C]/40 bg-[#FB923C]/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-[#FB923C] shrink-0 mt-0.5" />
          <div className="text-sm text-slate-200">
            <span className="font-semibold text-[#FB923C]">Trial key — values are scrambled.</span> The structure, rosters and inactives are live,
            but SportsDataIO masks the real status values on a free-trial key. Switch to a production key and these fill in automatically — no code change needed.
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading…</div>
      ) : !data?.available ? (
        <div data-testid="practice-unavailable" className="text-center py-16 text-slate-500 text-sm max-w-lg mx-auto">{data?.reason || "Report unavailable."}</div>
      ) : (data.teams || []).length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Nothing on the report yet.</div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4 text-[11px] font-mono text-slate-500">
            {data.season && <span>{data.season} · Week {data.week}</span>}
            <span>{data.count} players · {data.team_count} teams</span>
            <a href={data.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#38BDF8] hover:underline">
              {data.source} <ExternalLink size={10} />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.teams.map((g, i) => (
              <div key={g.team?.abbr || i} data-testid={`practice-team-${g.team?.abbr}`} className="rounded-xl bg-[#0F1420] border border-[#232D42] p-4">
                <div className="flex items-center gap-2.5 mb-2">
                  {g.team?.logo && <img src={g.team.logo} alt="" className="h-8 w-8 object-contain shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-heading font-bold text-slate-100 truncate">{g.team?.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{g.count} on report</div>
                  </div>
                </div>
                <div>{g.players.map((p, j) => <PlayerRow key={p.player_id || p.name || j} p={p} kind={data.kind} />)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="text-[11px] text-slate-600 mt-6 max-w-2xl leading-relaxed">
        Source: SportsDataIO licensed injury feed. {isNFL && "Practice designations are filed by teams Wed–Fri; game-day inactives update ~90 minutes before kickoff. "}Research only — not betting advice.
      </p>
    </div>
  );
}
